import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Col,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { getRequest, postRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import "../../../styles/Main.scss";
import { authUserData } from "../../../utils/Helper";

const { TextArea } = Input;

const mockDetail = {
  raId: "RA-2025-001",
  status: "Approved – Awaiting Shipment",
  source: "Gota Site Store",
  createdBy: "Site Incharge",
  date: "28-Dec-2025",
  intendedFor: "Apex Plumbing (Supplier Replacement)",
  shippingMethod: "Vendor Pickup",
  items: [
    {
      key: "1",
      itemName: "Basin Mixer Tap",
      sourceRef: "GRN-GOTA-001",
      qty: 5,
      uom: "pcs",
      notes: "QC Failed: Surface scratches confirmed.",
    },
  ],
  timeline: [
    "28-Dec 10:00 AM – Created by Site Incharge",
    "28-Dec 10:05 AM – Pending Central Procurement Approval (Current Step)",
  ],
};

const formatDisplayDate = (val) => {
  if (!val) return "—";
  if (typeof val !== "string") return String(val);
  const d = new Date(val);
  return Number.isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const ReturnAuthorizationDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: idFromPath } = useParams();
  const idFromState = location.state?.id ?? null;
  const id = idFromPath ?? idFromState;
  const [detail, setDetail] = useState(location.state?.detail || mockDetail);
  const [loading, setLoading] = useState(!!id);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedRa, setSelectedRa] = useState(detail);
  const [printing, setPrinting] = useState(false);
  const printIframeRef = useRef(null);
  const [buyerAction, setBuyerAction] = useState(null);
  const [planningForm] = Form.useForm();
  const watchedTargetLocation = Form.useWatch("targetLocation", planningForm);
  const companyId = authUserData()?.company ?? 1;
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [generatingRpo, setGeneratingRpo] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const actions = useMemo(
    () => [
      { key: "credit", label: "Issue Credit Note Only" },
      { key: "replacement", label: "Supplier Providing Replacement (FOC)" },
      { key: "reject", label: "Reject Return (Send back to site)" },
    ],
    [],
  );
  const actionCheckboxes = actions.map((a) => ({
    label: a.label,
    value: a.key,
  }));
  const buyerActionLabel =
    actions.find((a) => a.key === buyerAction)?.label ?? null;
  const planLocked =
    (detail?.buyer_action != null && String(detail.buyer_action).trim() !== "") ||
    detail?.target_delivery_location != null;
  const targetLocationDisplay =
    detail?.target_delivery_location_name ??
    deliveryLocations.find((d) => d?.id === detail?.target_delivery_location)
      ?.site_name ??
    deliveryLocations.find((d) => d?.id === detail?.target_delivery_location)
      ?.company_name ??
    "—";

  const buildPrintHtml = ({ values }) => {
    const copies = Math.max(1, Number(values?.copies) || 1);
    const labelSize = String(values?.labelSize || "");
    const pageCss =
      labelSize === '4" x 6" Roll'
        ? `@page { size: 4in 6in; margin: 0; }`
        : `@page { size: A4; margin: 12mm; }`;

    const safe = (v) =>
      String(v ?? "—")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");

    const qtyDisplay =
      detail?.items?.[0]?.qty != null
        ? `${detail.items[0].qty} ${detail?.items?.[0]?.uom ?? ""}`.trim()
        : "—";

    const labelHtml = Array.from({ length: copies })
      .map((_, i) => {
        const copyBadge =
          copies > 1
            ? `<span class="badge">Copy ${i + 1}/${copies}</span>`
            : "";
        return `<div class="label" style="${i < copies - 1 ? "margin-bottom: 12px;" : ""}">
  <div class="row"><div class="k">RA Ref</div><div class="v">${safe(detail?.raId)} ${copyBadge}</div></div>
  <div class="row"><div class="k">FROM</div><div class="v">${safe(detail?.source)}</div></div>
  <div class="row"><div class="k">TO</div><div class="v">${safe(detail?.intendedFor)}</div></div>
  <div class="row"><div class="k">Shipping method</div><div class="v">${safe(detail?.shippingMethod)}</div></div>
  <div class="hr"></div>
  <div class="row"><div class="k">Item</div><div class="v">${safe(detail?.items?.[0]?.itemName)}</div></div>
  <div class="row"><div class="k">Return Qty</div><div class="v">${safe(qtyDisplay)}</div></div>
  <div class="row"><div class="k">Notes</div><div class="v">${safe(detail?.items?.[0]?.notes)}</div></div>
  <div class="hr"></div>
  <!-- print settings removed from label -->
</div>`;
      })
      .join("");

    const isRoll = labelSize === '4" x 6" Roll';
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safe(`Shipping Label – ${detail?.raId ?? ""}`)}</title>
    <style>
      ${pageCss}
      body {
        font-family: Arial, Helvetica, sans-serif;
        margin: 0;
        padding: ${isRoll ? "0" : "16px"};
        display: flex;
        justify-content: center;
        align-items: flex-start;
        background: #fff;
      }
      .label {
        box-sizing: border-box;
        width: ${isRoll ? "100%" : "100%"};
        max-width: ${isRoll ? "4in" : "900px"};
        min-height: ${isRoll ? "6in" : "auto"};
        border: ${isRoll ? "none" : "1px solid #ddd"};
        border-radius: ${isRoll ? "0" : "6px"};
        padding: ${isRoll ? "24px" : "16px"};
        font-size: ${isRoll ? "14px" : "13px"};
        line-height: 1.35;
      }
      .row { display: flex; gap: 12px; margin-bottom: 10px; }
      .k { min-width: 150px; font-weight: 700; color: #333; }
      .v { flex: 1; color: #111; white-space: pre-wrap; word-break: break-word; }
      .hr { height: 1px; background: #eee; margin: 12px 0; }
      .muted { color: #666; font-size: 12px; }
      .badge { display: inline-block; border: 1px solid #ddd; padding: 2px 8px; border-radius: 999px; font-size: 12px; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    ${labelHtml}
    <script>
      window.addEventListener('load', function () {
        try { window.focus(); } catch (e) {}
        try { window.print(); } catch (e) {}
      });
    </script>
  </body>
</html>`;
  };

  const labelPreview = useMemo(() => {
    const item = detail?.items?.[0] || {};
    const qty =
      item?.qty != null ? `${item.qty} ${item?.uom ?? ""}`.trim() : "—";
    const rows = [
      ["RA Ref", detail?.raId ?? "—"],
      ["FROM", detail?.source ?? "—"],
      ["TO", detail?.intendedFor ?? "—"],
      ["Shipping method", detail?.shippingMethod ?? "—"],
      ["Item", item?.itemName ?? "—"],
      ["Return Qty", qty],
      ["Notes", item?.notes ?? "—"],
    ];
    return (
      <div
        style={{
          background: "#fafafa",
          padding: "12px 0",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            width: 420,
            maxWidth: "100%",
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            background: "#fff",
            padding: 12,
            marginLeft: 0,
          }}
        >
          {rows.map(([k, v]) => (
            <div
              key={k}
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 8,
                alignItems: "flex-start",
              }}
            >
              <div style={{ minWidth: 130, fontWeight: 700, color: "#333" }}>
                {k}
              </div>
              <div
                style={{
                  flex: 1,
                  color: "#111",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [detail]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const res = await getRequest(
          `delivery-address/${companyId}/get-address-by-company/`,
          { page: 1, ordering: "id", pagesize: 10 },
        );
        const payload = res?.data;
        const list =
          payload?.results?.data ?? payload?.data ?? payload?.results ?? [];
        setDeliveryLocations(Array.isArray(list) ? list : []);
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load delivery locations",
        );
        setDeliveryLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, [companyId]);

  useEffect(() => {
    if (!id) {
      setDetail(location.state?.detail || mockDetail);
      return;
    }
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await getRequest(`return-requests/${id}/`);
        const data = res?.data?.data ?? res?.data?.results ?? res?.data;
        if (data) {
          const raId = data.ra_id ?? data.raId ?? id;
          const dateStr = data.created_at ?? data.submitted_at;
          const dateDisplay =
            dateStr &&
            typeof dateStr === "string" &&
            /^\d{2}-\d{2}-\d{4}$/.test(dateStr)
              ? dateStr
              : formatDisplayDate(dateStr);
          const itemName = data.item_name ?? "—";
          const returnQty = data.return_qty ?? 0;
          const uom = data.uom ?? "";
          const sourceRef = data.source_ref ?? "—";
          const notes = data.notes ?? "";
          const items = [
            {
              key: "1",
              itemName,
              sourceRef,
              qty: returnQty,
              uom,
              notes,
            },
          ];
          const submittedAt = data.submitted_at
            ? new Date(data.submitted_at)
            : null;
          const timeline = [
            submittedAt && !Number.isNaN(submittedAt.getTime())
              ? `${formatDisplayDate(data.submitted_at)} – Submitted`
              : "Submitted",
            `${data.status ?? "Pending Approval"}`,
          ].filter(Boolean);
          setDetail({
            raId: String(raId),
            status: data.status ?? "Pending Approval",
            source: data.source_site ?? data.site_name ?? sourceRef ?? "—",
            createdBy: data.submitted_by_name ?? data.created_by ?? "—",
            date: dateDisplay,
            intendedFor: data.intended_destination ?? data.intended_for ?? "—",
            shippingMethod: data.shipping_method ?? "Vendor Pickup",
            supplierConfig: data.supplier_name ?? data.vendor_name ?? "—",
            buyer_action: data?.buyer_action ?? null,
            target_delivery_location: data?.target_delivery_location ?? null,
            target_delivery_location_name:
              data?.target_delivery_location_name ?? null,
            is_completed: !!data?.is_completed,
            buyer_notes: data?.buyer_notes ?? data?.notes ?? "",
            items,
            timeline,
          });
          setSelectedRa({ raId: String(raId) });
          const buyerActionKeyFromApi = (() => {
            const apiLabel = String(data?.buyer_action ?? "").trim();
            if (!apiLabel) return null;
            const matched = actions.find(
              (a) => String(a.label).trim() === apiLabel,
            );
            return matched?.key ?? null;
          })();
          if (buyerActionKeyFromApi) setBuyerAction(buyerActionKeyFromApi);
          planningForm.setFieldsValue({
            supplierConfig: data.supplier_name ?? data.vendor_name ?? "—",
            targetLocation: data?.target_delivery_location ?? undefined,
            buyerNotes: data?.buyer_notes ?? data?.notes ?? "",
          });
        }
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load return request details",
        );
        navigate("/return-requests-list");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id, navigate, planningForm, location.state]);

  useEffect(() => {
    setSelectedRa(detail);
  }, [detail]);
  const printDefaults = useMemo(
    () => ({
      labelSize: '4" x 6" Roll',
      copies: 1,
    }),
    [],
  );

  const infoColumns = [
    { title: "Item Name", dataIndex: "itemName", key: "itemName" },
    { title: "Source Ref.", dataIndex: "sourceRef", key: "sourceRef" },
    { title: "Qty.", dataIndex: "qty", key: "qty", width: 100 },
    { title: "UoM.", dataIndex: "uom", key: "uom", width: 80 },
    { title: "Site Notes", dataIndex: "notes", key: "notes" },
  ];

  const handleRejectRequest = () => {
    if (!id) {
      ShowErrorMsg("Return request ID is missing.");
      return;
    }
    if (buyerActionLabel !== "Reject Return (Send back to site)") {
      ShowErrorMsg('Please select "Reject Return (Send back to site)" first.');
      return;
    }

    Modal.confirm({
      title: "Reject this return request?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will reject the request and send it back to the site. Do you want to continue?",
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { danger: true, loading: rejecting },
      onOk: async () => {
        try {
          setRejecting(true);
          const res = await postRequest(
            `return-requests/${id}/reject-request/`,
          );
          message.success(
            res?.data?.message ?? "Return request rejected successfully",
          );
          navigate("/return-requests-list");
        } catch (err) {
          ShowErrorMsg(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to reject return request",
          );
        } finally {
          setRejecting(false);
        }
      },
    });
  };

  const handleGenerateRpo = () => {
    if (!id) {
      ShowErrorMsg("Return request ID is missing.");
      return;
    }
    if (!buyerActionLabel) {
      ShowErrorMsg("Please select buyer action.");
      return;
    }
    if (!planningForm.getFieldValue("targetLocation")) {
      ShowErrorMsg("Select target location.");
      return;
    }

    Modal.confirm({
      title: "Generate replacement PO (RPO)?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will generate a zero-value replacement PO for this return request. Do you want to continue?",
      okText: "OK",
      cancelText: "Cancel",
      okButtonProps: { loading: generatingRpo },
      onOk: async () => {
        try {
          const values = planningForm.getFieldsValue();
          const targetLocationId = values?.targetLocation ?? null;

          const payload = {
            buyer_action: buyerActionLabel,
            supplier_config:
              values?.supplierConfig ?? detail?.supplierConfig ?? "",
            target_delivery_location: targetLocationId,
            buyer_notes: values?.buyerNotes ?? "",
          };

          setGeneratingRpo(true);
          const res = await postRequest(
            `return-requests/${id}/generate-rpo/`,
            payload,
          );
          message.success(
            res?.data?.message ?? "Replacement PO generated successfully",
          );
          navigate("/return-requests-list");
        } catch (err) {
          ShowErrorMsg(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to generate RPO",
          );
        } finally {
          setGeneratingRpo(false);
        }
      },
    });
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Review and approve return authorization">
              <h3>Return Authorization Details – {detail.raId}</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/return-requests-list">Return Request Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{detail.raId}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div className="page-meta">
            <Tag
              color="orange"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                borderRadius: "5px",
                lineHeight: "25px",
                fontSize: "14px",
              }}
            >
              <ExclamationCircleOutlined /> {detail.status}
            </Tag>
          </div>
        </div>
      </div>
      <div className="stepscontentwrap">
        <div className="stepsContent">
        <section className="grid-sec mt-5">
        <Spin spinning={loading} tip="Loading...">
          <div className="FormBody">
            <div className="stepscontentwrap" style={{ padding: 0 }}>
              <div className="stepsContent">
                <div
                  className="formwrapper"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Divider
                    orientation="left"
                    plain
                    orientationMargin={0}
                    style={{ marginBottom: 0 }}
                  >
                    <div className="returninfo-title">
                      <span className="count">1</span> General Info
                    </div>
                  </Divider>

                  <div className="returninfo-details">
                    <div className="returninfo-row">
                      <strong>Source :</strong>
                      <span>{detail.source}</span>
                    </div>
                    
                    <div className="returninfo-row">
                      <strong>Date :</strong>
                      <span>{detail.date}</span>
                    </div>
                  </div>
                  <Divider
                    orientation="left"
                    plain
                    orientationMargin={0}
                    style={{ marginBottom: 0 }}
                  >
                    <div className="returninfo-title">
                      <span className="count">2</span> Destination Context
                    </div>
                  </Divider>

                  <div className="returninfo-details">
                    <div className="returninfo-row">
                      <strong>Intended for :</strong>
                      <span>{detail.intendedFor}</span>
                    </div>
                    <div className="returninfo-row">
                      <strong>Shipping Method :</strong>
                      <span>{detail.shippingMethod}</span>
                    </div>
                  </div>

                  <Divider
                    orientation="left"
                    plain
                    orientationMargin={0}
                    style={{ marginBottom: 0 }}
                  >
                    <div className="returninfo-title">
                      <span className="count">3</span> Returned Items
                    </div>
                  </Divider>

                  <div
                    className="tablewrp"
                    style={{
                      paddingLeft: "34px",
                      marginTop: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    <Table
                      rowKey="key"
                      columns={infoColumns}
                      dataSource={detail.items}
                      pagination={false}
                      size="small"
                      scroll={{ x: 900 }}
                    />
                  </div>

                  <Divider
                    orientation="left"
                    plain
                    orientationMargin={0}
                    style={{ marginBottom: 0 }}
                  >
                    <div className="returninfo-title">
                      <span className="count">4</span> Select Buyer Action &amp;
                      Plan Resolution
                    </div>
                  </Divider>

                  <div
                    style={{
                      paddingLeft: "34px",
                      marginTop: 10,
                      marginBottom: 16,
                    }}
                  >
                    {detail?.is_completed ? (
                      <Row gutter={[15, 15]}>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                          lg={8}
                          xl={8}
                          className="TextFeild gutter-row"
                        >
                          <Form.Item label="Buyer Action">
                            <Input
                              value={detail?.buyer_action ?? "—"}
                              readOnly
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                          lg={8}
                          xl={8}
                          className="TextFeild gutter-row"
                        >
                          <Form.Item label="Supplier Config">
                            <Input
                              value={detail?.supplierConfig ?? "—"}
                              readOnly
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                          lg={8}
                          xl={8}
                          className="TextFeild gutter-row"
                        >
                          <Form.Item label="Target Delivery Location for Replacement">
                            <Input value={targetLocationDisplay} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item label="Buyer Notes / Instructions">
                            <TextArea
                              value={detail?.buyer_notes ?? ""}
                              readOnly
                              autoSize={{ minRows: 2, maxRows: 6 }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : (
                      <>
                        <Checkbox.Group
                          style={{ fontSize: 14, fontWeight: 600 }}
                          options={actionCheckboxes}
                          disabled={planLocked}
                          value={buyerAction ? [buyerAction] : []}
                          onChange={(vals) => {
                            if (planLocked) return;
                            // AntD returns checked values in option order (not click order).
                            // We want single-select behavior with ability to deselect.
                            const next =
                              vals.length === 0
                                ? null
                                : vals.length === 1
                                  ? vals[0]
                                  : vals.find((v) => v !== buyerAction) ??
                                    vals[0];
                            setBuyerAction(next || null);
                          }}
                        />

                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            marginTop: 10,
                            marginBottom: 10,
                          }}
                        >
                          Replacement Planning Details (Linked to RPO Generation)
                        </div>

                        <Form
                          form={planningForm}
                          initialValues={{
                            supplierConfig: detail?.supplierConfig ?? "—",
                            targetLocation: undefined,
                            buyerNotes: "",
                          }}
                        >
                          <Row gutter={[15]}>
                            <Col
                              xs={24}
                              sm={24}
                              md={8}
                              lg={8}
                              xl={8}
                              className="TextFeild gutter-row"
                            >
                              <Form.Item
                                label="Supplier Config"
                                name="supplierConfig"
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col
                              xs={24}
                              sm={24}
                              md={8}
                              lg={8}
                              xl={8}
                              className="TextFeild gutter-row"
                            >
                              <Form.Item
                                label="Target Delivery Location for Replacement"
                                name="targetLocation"
                                rules={[
                                  {
                                    required: true,
                                    message: "Select target location",
                                  },
                                ]}
                              >
                                <Select
                                  loading={loadingLocations}
                                  disabled={planLocked}
                                  placeholder="Select target location"
                                  options={deliveryLocations.map((d) => ({
                                    label:
                                      d?.site_name ??
                                      d?.company_name ??
                                      d?.address_line_1 ??
                                      d?.address ??
                                      d?.id,
                                    value: d?.id,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24}>
                              <Form.Item
                                label="Buyer Notes / Instructions"
                                name="buyerNotes"
                              >
                                <TextArea
                                  readOnly
                                  autoSize={{ minRows: 2, maxRows: 6 }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="FormFooter">
                <div className="good-tge">
                  {!detail?.is_completed && (
                    <Alert
                      type="info"
                      showIcon
                      style={{
                        width: "100%",
                        padding: "5px 10px",
                        borderRadius: 4,
                      }}
                      message={
                        <span>
                          Approval updates will notify Central Procurement and
                          keep the item in Quarantine until carrier assignment
                          and shipment initiation.
                        </span>
                      }
                    />
                  )}
                </div>
                <Space style={{ justifyContent: "flex-end" }}>
                  {String(detail?.status || "").trim() !==
                    "Approved - Awaiting Shipment" && (
                    <>
                      <Tooltip
                        title={
                          !id
                            ? "Return request ID not found"
                            : buyerActionLabel !==
                                "Reject Return (Send back to site)"
                              ? 'Select "Reject Return (Send back to site)" to enable'
                              : ""
                        }
                      >
                        <span>
                          <Button
                            danger
                            loading={rejecting}
                            disabled={
                              !id ||
                              buyerActionLabel !==
                                "Reject Return (Send back to site)"
                            }
                            onClick={handleRejectRequest}
                          >
                            Reject Request
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        type="primary"
                        className="SaveGradiantBtn"
                        loading={generatingRpo}
                        disabled={
                          !id ||
                          !buyerActionLabel ||
                          !watchedTargetLocation
                        }
                        onClick={handleGenerateRpo}
                      >
                        Generate Zero-Value Replacement PO (RPO)
                      </Button>
                    </>
                  )}
                </Space>
              </div>
            </div>
          </div>
        </Spin>
      </section>
        </div>
        </div>        
      

      <Modal
        title={`Print Shipping Label – ${detail?.raId ?? "—"}`}
        open={isPrintModalOpen}
        onCancel={() => setIsPrintModalOpen(false)}
        footer={null}
        width={900}
      >
        <Divider
          orientation="left"
          plain
          orientationMargin={0}
          style={{ marginBottom: "5px", marginTop: "0px" }}
        >
          <div className="returninfo-title">Label Preview</div>
        </Divider>

        {labelPreview}

        <div className="FormFooter" style={{ padding: 0, marginTop: 16 }}>
          <Button
            type="primary"
            className="SaveGradiantBtn"
            block
            loading={printing}
            onClick={() => {
              try {
                setPrinting(true);
                const html = buildPrintHtml({ values: printDefaults });
                const iframe = printIframeRef.current;
                if (!iframe) {
                  ShowErrorMsg("Print preview is not ready.");
                  return;
                }
                iframe.srcdoc = html;
                message.success("Opening print dialog...");
                setIsPrintModalOpen(false);
              } finally {
                setPrinting(false);
              }
            }}
          >
            Print Label
          </Button>
        </div>

        <iframe
          ref={printIframeRef}
          title="print-iframe"
          style={{ width: 0, height: 0, border: 0, position: "absolute" }}
        />
      </Modal>
    </>
  );
};

export default ReturnAuthorizationDetail;
